import React from "react";
import styled from "styled-components";
import { FontWeight, form, transparentize } from "@glossy";

interface IProps {
  label?: string;
  checked?: boolean;
  allowExclusion?: boolean;
  onClick?: () => void;
  onClickCheck?: (checked: boolean) => void;
  isNeutral?: boolean;
  className?: string;
}

const Dash = styled(({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 14 14"
    width="14"
    height="14"
    className={className}
  >
    <line x1="3" y1="3" x2="11" y2="11" fill="none" strokeLinecap="square" />
  </svg>
))<{ active: boolean }>`
  stroke: ${(props) => props.active && form(props, "exclude")};
  stroke-width: ${(props) => (props.active ? 2 : 1)}px;
`;

const Square = styled(({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 14 14"
    width="14"
    height="14"
    className={className}
  >
    <rect x="4" y="4" width="6" height="6" strokeWidth="1" />
  </svg>
))`
  stroke: ${form};
  fill: ${form};
`;

const Check = styled(({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 14 14"
    width="14"
    height="14"
    className={className}
  >
    <path
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4 7.8c.9.7 1.6 1.6 2.2 2.5C7.2 8 8.4 5.8 10 3.8"
    />
  </svg>
))<{ active: boolean; isNeutral: boolean }>`
  stroke: ${(props) => props.active && form(props, "include")};
  stroke-width: ${(props) => (props.active ? 2 : 1)}px;
  display: ${(props) => props.isNeutral && "none"};
`;

const Box = styled.div<{ active: boolean; type: "include" | "exclude" }>`
  height: 18px;
  border-radius: 4px;
  border: ${(props) => (props.active ? 2 : 1)}px solid
    ${(props) =>
      props.active
        ? form(props, props.type)
        : transparentize(0.5, form(props, "primary"))};
  font-size: 0;
  line-height: 0;
  box-sizing: border-box;
  padding: ${(props) => !props.active && "1px"};
  margin-right: 4px;
  :hover {
    border-color: ${(props) => (props.active ? form(props, props.type) : form)};
    ${Dash} {
      stroke: ${(props) => form(props, "exclude")} !important;
      stroke-width: 2px !important;
    }
    ${Check} {
      stroke: ${(props) => form(props, "include")} !important;
      stroke-width: 2px !important;
    }
  }
`;

const DashBox = styled(Box)``;

const CheckBox = styled(Box)``;

const Root = styled.div<Partial<IProps>>`
  padding: 2px;
  line-height: 18px;
  cursor: pointer;
  display: flex;
  > * {
    display: inline-block;
    vertical-align: top;
  }
  :not(:hover) {
    ${DashBox} {
      opacity: ${(props) => props.checked !== false && 0};
      transform: translateX(22px);
    }
    ${CheckBox} {
      opacity: ${(props) => props.checked === false && 0};
    }
  }
  ${DashBox} {
    transition: ${(props) => props.checked === false && "transform 0.2s"};
  }
  :hover {
    ${Square} {
      display: none;
    }
    ${Dash} {
      stroke: ${(props) =>
        props.checked === false ? form(props, "exclude") : form};
      stroke-width: ${(props) => (props.checked === false ? 2 : 1)}px;
    }
    ${Check} {
      display: block;
      stroke: ${(props) =>
        props.checked === true ? form(props, "include") : form};
      stroke-width: ${(props) => (props.checked === true ? 2 : 1)}px;
    }
  }
`;

const Label = styled.span<Partial<IProps>>`
  margin-left: 2px;
  font-size: 14px;
  font-weight: ${(props) =>
    props.checked !== undefined && props.checked !== null && FontWeight.Bold};
  color: ${(props) =>
    form(
      props,
      props.checked === true
        ? "include"
        : props.checked === false
        ? "exclude"
        : "primary"
    )};
  text-decoration: ${(props) => props.checked === false && "line-through"};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UiKitFormTribox = ({
  label,
  allowExclusion = true,
  checked,
  onClick,
  onClickCheck,
  isNeutral = false,
  className,
}: IProps) => (
  <Root checked={checked} onClick={onClick} className={className}>
    {allowExclusion && (
      <DashBox
        active={checked === false}
        type="exclude"
        onClick={(e) => {
          e.stopPropagation();
          onClickCheck && onClickCheck(false);
        }}
      >
        <Dash active={checked === false} />
      </DashBox>
    )}
    <CheckBox
      active={checked === true && !isNeutral}
      type="include"
      onClick={(e) => {
        e.stopPropagation();
        onClickCheck && onClickCheck(true);
      }}
    >
      <Check active={checked === true} isNeutral={isNeutral} />
      {isNeutral && <Square />}
    </CheckBox>
    {label && <Label checked={checked}>{label}</Label>}
  </Root>
);

export default UiKitFormTribox;
